import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Users, UserPlus, X, Copy, Check, Mail } from "lucide-react";
import { toast } from "sonner";

export default function TeamSeats() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const load = () => {
    api.get("/team/members").then((r) => {
      setMembers(r.data.members || []);
      setPending(r.data.pending_invites || []);
    });
  };

  useEffect(load, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/team/invite", { email });
      toast.success(`Invitation sent to ${email}`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invite failed");
    } finally { setLoading(false); }
  };

  const revoke = async (id) => {
    await api.delete(`/team/invites/${id}`);
    toast.success("Invitation revoked");
    load();
  };

  const copyLink = (code) => {
    const link = `${window.location.origin}/accept-invite/${code}`;
    navigator.clipboard.writeText(link);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2500);
    toast.success("Invite link copied");
  };

  const isOwner = user?.role === "owner";

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 mt-8">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-[#2C5530]"/>
        <div className="text-sm font-medium">Team seats</div>
        <Badge variant="outline" className="ml-1 text-xs border-stone-200">{members.length} member{members.length !== 1 ? "s" : ""}</Badge>
      </div>
      <p className="text-sm text-stone-600 mb-5">
        {isOwner ? "Invite teammates so multiple people can retire credits under your organization." : "Members of your team can view and add to the same portfolio."}
      </p>

      {isOwner && (
        <form onSubmit={invite} className="flex gap-2 mb-5">
          <Input type="email" required placeholder="teammate@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" className="h-10 bg-stone-50 border-stone-200"/>
          <Button type="submit" disabled={loading} data-testid="invite-btn" className="h-10 bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full px-5">
            <UserPlus className="w-4 h-4 mr-1"/> {loading ? "…" : "Invite"}
          </Button>
        </form>
      )}

      <div className="space-y-2">
        {members.map((m) => (
          <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-stone-50">
            <div className="w-9 h-9 rounded-full bg-[#2C5530]/10 text-[#2C5530] flex items-center justify-center text-sm font-medium">
              {(m.email || "?")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{m.email}</div>
              <div className="text-xs text-stone-500 capitalize">{m.role || "member"}</div>
            </div>
            {m.role === "owner" && <Badge className="bg-[#2C5530]/10 text-[#2C5530] border-0 hover:bg-[#2C5530]/10">Owner</Badge>}
          </div>
        ))}

        {pending.map((p) => (
          <div key={p.id} data-testid={`pending-${p.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100">
            <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Mail className="w-4 h-4"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{p.email}</div>
              <div className="text-xs text-stone-500">Pending · code {p.code}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => copyLink(p.code)} data-testid={`copy-invite-${p.id}`}>
              {copiedCode === p.code ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>}
            </Button>
            {isOwner && (
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => revoke(p.id)} data-testid={`revoke-${p.id}`}>
                <X className="w-4 h-4"/>
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
